import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * The phone's answer to a side panel.
 *
 * On a wide screen a panel takes a column and the world simply gets narrower.
 * A phone has no column to give: the same panel would leave a strip of globe
 * too small to read, so the panel comes up over the world instead, and the
 * world stays whole behind it.
 *
 * It behaves the way a sheet on a phone is expected to: two heights, drag the
 * handle to move between them, drag it down or tap the world behind to put it
 * away. Nothing here runs on a desktop.
 */
export interface SheetProps {
  /** Title for assistive tech; the panel inside draws its own header. */
  label: string;
  onClose: () => void;
  children: ReactNode;
}

const PEEK = 0.55;
const FULL = 0.92;

export function Sheet({ label, onClose, children }: SheetProps) {
  const [expanded, setExpanded] = useState(false);
  const [drag, setDrag] = useState(0);
  const sheet = useRef<HTMLDivElement>(null);
  const start = useRef<number | null>(null);

  // Opening a sheet over the world should not leave the page behind it
  // scrollable — on iOS that reads as the whole app coming loose.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const height = `calc(${(expanded ? FULL : PEEK) * 100}vh - var(--safe-bottom))`;

  return (
    <div className="fixed inset-0 z-40" role="dialog" aria-modal="true" aria-label={label}>
      {/* The world is still there, and still tappable to get back to it. */}
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 w-full"
        style={{ background: 'color-mix(in oklch, var(--space-deep) 45%, transparent)' }}
      />

      <div
        ref={sheet}
        className="absolute inset-x-0 bottom-0 flex flex-col overflow-hidden rounded-t-[var(--radius-lg)]"
        style={{
          height,
          transform: `translateY(${drag}px)`,
          transition: start.current === null ? 'height var(--dur-panel) var(--ease-out), transform var(--dur-quick) var(--ease-out)' : 'none',
          background: 'var(--surface-0)',
          borderTop: '1px solid var(--border)',
          boxShadow: 'var(--shadow-panel)',
          paddingBottom: 'var(--safe-bottom)',
        }}
      >
        <div
          className="flex shrink-0 cursor-grab touch-none items-center justify-center py-3"
          onPointerDown={(event) => {
            start.current = event.clientY;
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            if (start.current === null) return;
            // Up past the handle's own travel just expands it; down follows the
            // finger, so dismissing is the same gesture everywhere else on the
            // phone.
            setDrag(Math.max(-40, event.clientY - start.current));
          }}
          onPointerUp={(event) => {
            const moved = start.current === null ? 0 : event.clientY - start.current;
            start.current = null;
            setDrag(0);
            if (moved > 110) onClose();
            else if (moved < -30) setExpanded(true);
            else if (moved > 30) setExpanded(false);
          }}
        >
          <span
            aria-hidden
            className="block h-1 w-10 rounded-full"
            style={{ background: 'var(--border)' }}
          />
        </div>

        <div className="min-h-0 flex-1">{children}</div>
      </div>
    </div>
  );
}
